import { type MaybeRef, toValue } from "vue";
import { computed } from "vue";
import type { IPromoProduct } from "@magnit/unit-catalog/src/unit-catalog.types";
import type { ICatalogBFFDetailedGood } from "../types/goods";
import { CatalogBFFDetailType } from "../types/goods";
import { formatPrice } from "@magnit/unit-catalog/src/utils/converter";

const LABEL_DESCRIPTION = "Описание";

export default (product: MaybeRef<ICatalogBFFDetailedGood | IPromoProduct>) => {
  const productValue = toValue(product);

  // TODO: возможно имеет смысл раздробить этот один большой композабл useProduct на несколько более специфичных
  // некоторые сущности тут используются только разово в компонентах
  // однако чтобы к ним достучаться вызывается целый громадный композабл.
  // Надо вычленить какие-то более мелкие куски, и как раз в духе композаблов они должны друг друга использовать (композить).
  // А не делать один громаднейший композабл.

  const productId = computed(() => productValue.id);

  const productName = computed(() => productValue.name);

  const isAdult = computed(
    () => (productValue as ICatalogBFFDetailedGood).isForAdults,
  );

  const productBadges = computed(
    () => (productValue as ICatalogBFFDetailedGood)?.badges ?? [],
  );

  const productPromotion = computed(
    () => (productValue as ICatalogBFFDetailedGood)?.promotion ?? null,
  );

  const productPrice = computed(() => {
    const price = (productValue as IPromoProduct | ICatalogBFFDetailedGood)
      ?.price;
    return price ? price / 100 : null;
  });

  const productOldPrice = computed(() => {
    if ("oldPrice" in productValue && productValue.oldPrice)
      return productValue.oldPrice / 100;
    if (productPromotion.value?.oldPrice)
      return productPromotion.value?.oldPrice / 100;
    return null;
  });

  const productDiscountPercent = computed(() => {
    if (productPromotion.value?.discountPercent)
      return productPromotion.value?.discountPercent;
    return null;
  });

  const productDiscountPercentLabel = computed(() => {
    const label = (productValue as IPromoProduct)?.discountLabel ?? null;
    if (label) return label;
    return productDiscountPercent.value
      ? `-${productDiscountPercent.value}%`
      : null;
  });

  const productDetails = computed(
    () => (productValue as ICatalogBFFDetailedGood)?.details ?? null,
  );

  const productDetailsDescription = computed(() => {
    return (productValue as IPromoProduct)?.description ?? null;
  });

  const productGallery = computed(() =>
    ((productValue as ICatalogBFFDetailedGood)?.gallery || [])
      .filter((i) => i.type === "IMAGE")
      .map((i) => i.url),
  );

  const productImageUrl = computed(
    () => (productValue as IPromoProduct)?.imageUrl ?? null,
  );

  const productCategories = computed(
    () => (productValue as ICatalogBFFDetailedGood)?.categories ?? [],
  );

  const productArticleCategory = computed(
    () => (productValue as IPromoProduct)?.articleCategory ?? null,
  );

  const productDiscountCategory = computed(
    () => (productValue as IPromoProduct)?.discountCategory ?? null,
  );

  const productDiscountBadge = computed(() => {
    if (!productDiscountCategory.value) return null;
    return {
      backgroundColor: "var(--u-catalog-product-card-discount-background)",
      textColor: "var(--u-catalog-product-card-discount-color)",
      text: productDiscountCategory.value,
    };
  });

  const productProfitBadge = computed(() => {
    if (!("profit" in productValue && productValue?.profit)) return null;
    return {
      text: `Экономия до ${formatPrice(productValue.profit / 100)}` as string,
    };
  });

  const productNutritionFacts = computed(() =>
    productDetails.value?.find(
      (i) => i.type === CatalogBFFDetailType.nutritionFactsType,
    ),
  );

  const productDetailsParameters = computed(() => {
    const result =
      productDetails.value
        ?.filter((i) => i.type !== CatalogBFFDetailType.nutritionFactsType)
        .map((i) => {
          if (i.type !== CatalogBFFDetailType.stringType) return i;
          return {
            ...i,
            parameters: [
              {
                name: i.name,
                value: i.value || "",
              },
            ],
          };
        }) || [];
    if (productDetailsDescription.value) {
      result.push({
        name: LABEL_DESCRIPTION,
        parameters: [
          {
            name: LABEL_DESCRIPTION,
            value: productDetailsDescription.value,
          },
        ],
        type: CatalogBFFDetailType.stringType,
      });
    }
    return result;
  });

  const productWeight = computed(() => {
    if ("weighted" in productValue) {
      return productValue.weighted.shelfLabel || "";
    }
    if ("unitValue" in productValue) {
      return productValue.unitValue || "";
    }
    return "";
  });

  const productUnitWeight = computed(() => {
    if ("weighted" in productValue) {
      const { isWeighted, unitLabel, unitPrice } = productValue.weighted;
      return isWeighted
        ? `${formatPrice((unitPrice ?? 0) / 100)}/${unitLabel}`
        : "";
    }
    return "";
  });

  const inStock = computed(() => {
    if ("quantity" in productValue) {
      return !!productValue.quantity;
    }
    return false;
  });
  const isPromo = computed(() => !("quantity" in productValue));
  const showNotInStock = computed(() => !isPromo.value && !inStock.value);

  return {
    productId,
    productName,
    productPrice,
    productOldPrice,
    productDiscountPercent,
    productDiscountPercentLabel,
    productDetails,
    productDetailsDescription,
    productGallery,
    productImageUrl,
    productCategories,
    productArticleCategory,
    productDiscountCategory,
    productDiscountBadge,
    productProfitBadge,
    productNutritionFacts,
    productDetailsParameters,
    productBadges,
    isAdult,
    productWeight,
    productUnitWeight,
    inStock,
    showNotInStock,
  };
};
